import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertBetaAccess } from "@/lib/beta-access";
import { requireCompany } from "@/lib/company-access";

// Teams: invite colleagues into the company workspace by email. Invites carry
// a single-use token; accepting joins the caller as company member with the
// invited role. Owner = companies.user_id; only the owner manages the team.

const INVITE_TTL_DAYS = 14;
const MAX_TEAM_SIZE = 10;

export const listTeam = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertBetaAccess(context.supabase, context.userId);
    const access = await requireCompany(context.supabase, context.userId);

    const { data: members } = await context.supabase
      .from("company_members")
      .select("id, user_id, role, created_at")
      .eq("company_id", access.id)
      .order("created_at", { ascending: true });

    // Resolve member emails via admin (auth.users is not client-readable).
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const enriched = await Promise.all(
      (members ?? []).map(async (m) => {
        const { data } = await supabaseAdmin.auth.admin.getUserById(m.user_id);
        return { ...m, email: data?.user?.email ?? "—" };
      }),
    );

    // Pending invites are only visible to the owner (RLS).
    const { data: invites } = await context.supabase
      .from("company_invites")
      .select("id, email, role, created_at, accepted_at")
      .eq("company_id", access.id)
      .is("accepted_at", null)
      .order("created_at", { ascending: false });

    return { members: enriched, invites: invites ?? [], role: access.role };
  });

export const inviteMember = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z
      .object({
        email: z.string().trim().email().max(200),
        role: z.enum(["admin", "member"]).default("member"),
      })
      .parse(d),
  )
  .handler(async ({ context, data }) => {
    await assertBetaAccess(context.supabase, context.userId);
    const access = await requireCompany(context.supabase, context.userId);
    if (access.role !== "owner") throw new Error("Only the owner can invite team members.");

    const { count: memberCount } = await context.supabase
      .from("company_members")
      .select("id", { count: "exact", head: true })
      .eq("company_id", access.id);
    if ((memberCount ?? 0) >= MAX_TEAM_SIZE) {
      throw new Error(`Team limit reached (${MAX_TEAM_SIZE} members).`);
    }

    const email = data.email.toLowerCase();
    const { data: company } = await context.supabase
      .from("companies")
      .select("name")
      .eq("id", access.id)
      .maybeSingle();

    // Reuse a pending invite for the same address instead of stacking tokens.
    const { data: existing } = await context.supabase
      .from("company_invites")
      .select("id, token")
      .eq("company_id", access.id)
      .eq("email", email)
      .is("accepted_at", null)
      .maybeSingle();

    let token = existing?.token;
    if (!token) {
      const { data: invite, error } = await context.supabase
        .from("company_invites")
        .insert({
          company_id: access.id,
          email,
          role: data.role,
          invited_by: context.userId,
        })
        .select("token")
        .single();
      if (error) throw error;
      token = invite.token;
    }

    const joinUrl = `https://realitytwin.io/join/${token}`;
    try {
      const { enqueueTransactionalEmail, baseEmailTemplate } = await import("@/lib/notify.server");
      await enqueueTransactionalEmail({
        to: email,
        subject: `You've been invited to ${company?.name ?? "a company"} on Reality Twin`,
        html: baseEmailTemplate(
          "Join your team on Reality Twin",
          `<p>You've been invited to join <strong>${company?.name ?? "a company"}</strong>'s workspace on Reality Twin — the AI Twin that answers customers, captures leads and closes deals for your company.</p><p>The invite is valid for ${INVITE_TTL_DAYS} days.</p>`,
          { url: joinUrl, label: "Accept invitation" },
        ),
        text: `Join ${company?.name ?? "a company"} on Reality Twin: ${joinUrl}`,
        label: "team_invite",
      });
    } catch (e) {
      console.error("invite email failed:", e);
    }

    return { ok: true };
  });

export const revokeInvite = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    await assertBetaAccess(context.supabase, context.userId);
    const { error } = await context.supabase.from("company_invites").delete().eq("id", data.id);
    if (error) throw error;
    return { ok: true };
  });

export const removeMember = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    await assertBetaAccess(context.supabase, context.userId);
    const access = await requireCompany(context.supabase, context.userId);
    if (access.role !== "owner") throw new Error("Only the owner can remove team members.");
    const { error } = await context.supabase.from("company_members").delete().eq("id", data.id);
    if (error) throw error;
    return { ok: true };
  });

export const acceptInvite = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ token: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    // Token lookup runs with the service role: the invitee has no RLS access
    // to the invite row before becoming a member.
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: invite } = await supabaseAdmin
      .from("company_invites")
      .select("id, company_id, role, created_at, accepted_at, companies(name)")
      .eq("token", data.token)
      .maybeSingle();
    if (!invite) throw new Error("This invitation does not exist.");
    if (invite.accepted_at) throw new Error("This invitation was already used.");
    const ageDays = (Date.now() - Date.parse(invite.created_at)) / 86_400_000;
    if (ageDays > INVITE_TTL_DAYS) throw new Error("This invitation has expired.");

    // Owners cannot also join someone else's workspace as a member — it would
    // tangle the shared credit pool and active-twin model. A user may own
    // several twins, so list them (never .maybeSingle() on user_id, which
    // throws with 2+ owned twins) and block if any differ from the invite.
    const { data: owned } = await supabaseAdmin
      .from("companies")
      .select("id")
      .eq("user_id", context.userId);
    if ((owned ?? []).some((c: any) => c.id !== invite.company_id)) {
      throw new Error("This account already owns a workspace. Use a different account to join.");
    }

    const { error } = await supabaseAdmin.from("company_members").upsert(
      {
        company_id: invite.company_id,
        user_id: context.userId,
        role: invite.role === "admin" ? "admin" : "member",
      },
      { onConflict: "company_id,user_id", ignoreDuplicates: true },
    );
    if (error) throw error;

    await supabaseAdmin
      .from("company_invites")
      .update({ accepted_at: new Date().toISOString(), accepted_by: context.userId })
      .eq("id", invite.id);

    return { ok: true, companyName: (invite as any).companies?.name ?? "your team" };
  });
